import React, { useState } from 'react';
import { Activity, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * PerformanceMetrics - Display API timing and performance data
 *
 * Shows timing_ms from structured logs, color-coded by performance target:
 * - < 100ms = green (meets target)
 * - 100-500ms = amber (acceptable)
 * - > 500ms = red (needs optimization)
 */
const PerformanceMetrics = ({ metrics = {} }) => {
  const [expanded, setExpanded] = useState(false);

  /**
   * Format duration in human-readable form
   */
  const formatDuration = (ms) => {
    if (ms === null || ms === undefined) return 'N/A';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  /**
   * Get color class based on performance thresholds
   */
  const getPerformanceColor = (ms) => {
    if (ms === null || ms === undefined) return 'text-zinc-400';
    if (ms < 100) return 'text-green-400';
    if (ms < 500) return 'text-amber-400';
    return 'text-red-400';
  };

  // Calculate average API response time
  const apiTimes = Object.entries(metrics)
    .filter(([key, val]) => typeof val === 'number' && key.includes('Time'))
    .map(([_, val]) => val);

  const avgResponseTime = apiTimes.length > 0
    ? apiTimes.reduce((sum, val) => sum + val, 0) / apiTimes.length
    : 0;

  return (
    <div className="relative">
      {/* Collapsed view - just the average */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors border border-zinc-700"
        aria-label="Toggle performance metrics"
      >
        <Activity className="text-cyan-400" size={16} />
        <span className={`text-sm font-mono ${getPerformanceColor(avgResponseTime)}`}>
          {formatDuration(avgResponseTime)}
        </span>
        {expanded ? (
          <ChevronUp className="text-zinc-400" size={14} />
        ) : (
          <ChevronDown className="text-zinc-400" size={14} />
        )}
      </button>

      {/* Expanded metrics panel */}
      {expanded && (
        <div className="absolute top-full right-0 mt-2 bg-zinc-900 border border-cyan-500/30 rounded-lg shadow-2xl p-4 min-w-64 z-50">
          <h4 className="text-cyan-400 font-semibold text-sm mb-3 flex items-center gap-2">
            <Activity size={16} />
            Performance Metrics
          </h4>

          <div className="space-y-2 text-sm">
            {/* Last track time */}
            {metrics.lastTrackTime !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Last Track:</span>
                <span className={`font-mono ${getPerformanceColor(metrics.lastTrackTime)}`}>
                  {formatDuration(metrics.lastTrackTime)}
                </span>
              </div>
            )}

            {/* Last skeletal load time */}
            {metrics.lastSkeletalLoadTime !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Last Skeletal Load:</span>
                <span className={`font-mono ${getPerformanceColor(metrics.lastSkeletalLoadTime)}`}>
                  {formatDuration(metrics.lastSkeletalLoadTime)}
                </span>
              </div>
            )}

            {/* Last novel view render time */}
            {metrics.lastNovelViewTime !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Last Novel View:</span>
                <span className={`font-mono ${getPerformanceColor(metrics.lastNovelViewTime)}`}>
                  {formatDuration(metrics.lastNovelViewTime)}
                </span>
              </div>
            )}

            {/* Average response time */}
            {apiTimes.length > 0 && (
              <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                <span className="text-zinc-400">Avg Response:</span>
                <span className={`font-mono ${getPerformanceColor(avgResponseTime)}`}>
                  {formatDuration(avgResponseTime)}
                </span>
              </div>
            )}

            {/* Frame rate (if tracking) */}
            {metrics.fps !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">FPS:</span>
                <span className="text-white font-mono">
                  {metrics.fps.toFixed(1)}
                </span>
              </div>
            )}

            {/* Cache hit rate (if available) */}
            {metrics.cacheHitRate !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Cache Hit Rate:</span>
                <span className="text-white font-mono">
                  {(metrics.cacheHitRate * 100).toFixed(0)}%
                </span>
              </div>
            )}
          </div>

          {/* Performance legend */}
          <div className="mt-4 pt-3 border-t border-zinc-700 text-xs text-zinc-500 space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span>&lt; 100ms (Target met)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span>100-500ms (Acceptable)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-400" />
              <span>&gt; 500ms (Needs optimization)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMetrics;
